import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, where, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { generateTicketId as generateTicketIdUtil, generateQRCode } from '../utils/ticketUtils';
import { sendTicketEmail } from '../utils/emailService';
import { generateTicketImage } from '../utils/ticketImageGenerator';
import { subscribeToEventSettings, getDefaultEventSettings } from '../utils/settingsService';
import Ticket from '../components/Ticket';
import './GenerateTicketPage.css';
import Swal from 'sweetalert2';

function GenerateTicketPage() {
  const navigate = useNavigate();
  const defaultEventSettings = getDefaultEventSettings();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [eventType, setEventType] = useState(defaultEventSettings.eventName);
  const [notes, setNotes] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewTicketData, setPreviewTicketData] = useState(null);
  const [previewQRCode, setPreviewQRCode] = useState(null);
  const [massGenerationMode, setMassGenerationMode] = useState(false);
  const [selectedUsersForMass, setSelectedUsersForMass] = useState([]);
  const [generatingMass, setGeneratingMass] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [openTicketActionId, setOpenTicketActionId] = useState(null);
  const previewTicketRef = useRef(null);
  const [eventSettings, setEventSettings] = useState(defaultEventSettings);

  // Helper: send ticket details via WhatsApp to the user's phone number
  const sendTicketViaWhatsApp = (ticketData, rawPhoneNumber) => {
    if (!rawPhoneNumber) {
      console.warn('No phone number available for WhatsApp ticket delivery');
      return;
    }

    // Normalize phone number for WhatsApp "wa.me" format (expects international format without "+")
    let phone = String(rawPhoneNumber).replace(/\D/g, '');

    // If number starts with 0 and looks like a Nigerian mobile (11 digits), convert to 234XXXXXXXXXX
    if (phone.startsWith('0') && phone.length === 11) {
      phone = '234' + phone.slice(1);
    }

    if (!phone) {
      console.warn('Unable to normalize phone number for WhatsApp:', rawPhoneNumber);
      return;
    }

    const {
      userName,
      ticketId,
      eventName,
      eventDate,
      eventTime,
      eventLocation
    } = ticketData || {};

    const messageLines = [
      `Hello ${userName || ''},`,
      '',
      `Your ticket for *${eventName || 'MTU Alumni Event'}* is confirmed ✅`,
      '',
      `*Ticket ID:* ${ticketId}`,
      `*Date:* ${eventDate || ''}`,
      `*Time:* ${eventTime || ''}`,
      `*Venue:* ${eventLocation || ''}`,
      '',
      'Please keep this message safe.',
      'Your full ticket (with QR code) has also been sent to your email address.',
      '',
      'Powered by MTU Alumni Association.'
    ];

    const message = messageLines.join('\n');
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp chat in a new tab/window so the admin can send the message
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified
        });
        fetchRegistrations();
        fetchTickets();
      } else {
        navigate('/admin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const unsubscribeSettings = subscribeToEventSettings((settings) => {
      setEventSettings(settings);
    });

    return () => {
      if (unsubscribeSettings) {
        unsubscribeSettings();
      }
    };
  }, []);

  useEffect(() => {
    if (eventSettings?.eventName) {
      setEventType(eventSettings.eventName);
    }
  }, [eventSettings?.eventName]);

  useEffect(() => {
    if (!openTicketActionId) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest('.ticket-action-menu-container')) {
        setOpenTicketActionId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openTicketActionId]);

  // Calculate dropdown position to open upward if near bottom of screen
  useEffect(() => {
    if (!openTicketActionId) {
      return;
    }

    const calculatePosition = () => {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const container = document.querySelector(`[data-ticket-id="${openTicketActionId}"]`);
        if (!container) {
          // Retry after a short delay if element not found
          setTimeout(calculatePosition, 50);
          return;
        }

        const button = container.querySelector('.ticket-action-menu-button');
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        // const dropdownHeight = 120; // Approximate dropdown height with padding
        const spaceBelow = viewportHeight - rect.bottom;
        const buttonBottom = rect.bottom;
        
        if (buttonBottom > viewportHeight * 0.6 || spaceBelow < 150) {
          // You can add logic here if you decide to use dropdownOpenUpward state
        }
      });
    };

    // Calculate immediately and after a short delay to ensure DOM is ready
    calculatePosition();
    const timeoutId = setTimeout(calculatePosition, 10);
    
    const handleResize = () => calculatePosition();
    const handleScroll = () => calculatePosition();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openTicketActionId]);

  const fetchRegistrations = () => {
    try {
      const registrationsRef = collection(db, 'registrations');
      const q = query(registrationsRef, orderBy('registeredAt', 'desc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const registrationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRegistrations(registrationsData);
        setFilteredUsers(registrationsData);
      }, (error) => {
        console.error('Error fetching registrations:', error);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up registrations listener:', error);
    }
  };

  const fetchTickets = () => {
    try {
      const ticketsRef = collection(db, 'tickets');
      const q = query(ticketsRef, orderBy('generatedAt', 'desc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const ticketsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTickets(ticketsData);
        setLoading(false);
      }, (error) => {
        console.error('Error fetching tickets:', error);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up tickets listener:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Filter to only show paid registrations
    const paidRegistrations = registrations.filter(reg => 
      reg.status === 'paid' || reg.status === 'confirmed'
    );

    if (userSearch.trim() === '') {
      setFilteredUsers(paidRegistrations);
    } else {
      const filtered = paidRegistrations.filter(reg =>
        reg.fullName?.toLowerCase().includes(userSearch.toLowerCase()) ||
        reg.email?.toLowerCase().includes(userSearch.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [userSearch, registrations]);

  const handleUserSelect = (registration) => {
    setSelectedUser(registration);
    setUserSearch(registration.fullName || registration.email);
    setShowUserDropdown(false);
  };

  const generateTicketId = () => {
    return generateTicketIdUtil();
  };

  // Update preview when user or event type changes
  useEffect(() => {
    const updatePreview = async () => {
      if (selectedUser && eventType) {
        const existingTicket = tickets.find(
          (ticket) => ticket.userEmail === selectedUser.email
        );

        if (existingTicket) {
          setPreviewTicketData(existingTicket);
          setPreviewQRCode(existingTicket.qrCodeDataUrl);
          return;
        }

        // Generate a ticket ID for preview
        const ticketId = generateTicketId();
        
        try {
          const qrCodeDataUrl = await generateQRCode(ticketId);
          const ticketData = {
            ticketId,
            userName: selectedUser.fullName || selectedUser.email,
            userEmail: selectedUser.email,
            eventType,
            eventName: eventSettings?.eventName || defaultEventSettings.eventName,
            eventDate: eventSettings?.eventDate || defaultEventSettings.eventDate,
            eventTime: eventSettings?.eventTime || defaultEventSettings.eventTime,
            eventLocation: eventSettings?.eventLocation || defaultEventSettings.eventLocation
          };
          setPreviewTicketData(ticketData);
          setPreviewQRCode(qrCodeDataUrl);
        } catch (error) {
          console.error('Error generating preview QR code:', error);
        }
      } else {
        setPreviewTicketData(null);
        setPreviewQRCode(null);
      }
    };

    updatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, eventType, tickets]);

  // Check if ticket already exists for a user
  const checkTicketExists = async (userEmail) => {
    try {
      const ticketsRef = collection(db, 'tickets');
      const q = query(ticketsRef, where('userEmail', '==', userEmail));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking ticket existence:', error);
      return false;
    }
  };

  const handleGenerateTicket = async () => {
    if (!selectedUser || !eventType) {
      await Swal.fire({
        icon: 'warning',
        title: 'Missing information',
        text: 'Please select a user and event type before generating a ticket.',
        confirmButtonColor: '#f59e0b'
      });
      return;
    }

    // Check if ticket already exists
    const ticketExists = await checkTicketExists(selectedUser.email);
    if (ticketExists) {
      await Swal.fire({
        icon: 'info',
        title: 'Ticket already exists',
        text: `A ticket has already been generated for ${selectedUser.fullName || selectedUser.email}.`,
        confirmButtonColor: '#003399'
      });
      return;
    }

    // Use preview ticket data if available, otherwise generate new
    if (!previewTicketData || !previewQRCode) {
      await Swal.fire({
        icon: 'info',
        title: 'Generating preview',
        text: 'Please wait for the ticket preview to load before generating.',
        confirmButtonColor: '#003399'
      });
      return;
    }

    setGenerating(true);
    try {
      // Use the preview ticket data (already generated)
      const ticketId = previewTicketData.ticketId;
      const qrCodeDataUrl = previewQRCode;
      
      const ticketData = {
        ticketId,
        registrationId: selectedUser.id,
        userId: selectedUser.id || selectedUser.email,
        userName: selectedUser.fullName || selectedUser.email,
        userEmail: selectedUser.email,
        eventType,
        eventName: eventSettings?.eventName || defaultEventSettings.eventName,
        eventDate: eventSettings?.eventDate || defaultEventSettings.eventDate,
        eventTime: eventSettings?.eventTime || defaultEventSettings.eventTime,
        eventLocation: eventSettings?.eventLocation || defaultEventSettings.eventLocation,
        status: 'active',
        used: false,
        qrCodeDataUrl: qrCodeDataUrl,
        notes: notes || '',
        generatedAt: serverTimestamp(),
        generatedBy: user?.email || 'admin'
      };

      // Save ticket to Firestore
      await addDoc(collection(db, 'tickets'), ticketData);
      
      // Generate ticket image from preview (wait for DOM to be ready)
      let ticketImageDataUrl = null;
      await new Promise(resolve => setTimeout(resolve, 300)); // Small delay for DOM update
      if (previewTicketRef.current) {
        try {
          // Find the actual ticket element inside the wrapper
          const ticketElement = previewTicketRef.current.querySelector('.ticket');
          if (ticketElement) {
            ticketImageDataUrl = await generateTicketImage(
              ticketElement,
              'jpeg',
              0.45,
              0.35
            );
          }
        } catch (imageError) {
          console.error('Error generating ticket image:', imageError);
          // Continue even if image generation fails
        }
      }

      // Send email with ticket
      try {
        const emailSent = await sendTicketEmail({
          ...ticketData,
          qrCodeDataUrl: qrCodeDataUrl,
          ticketImageDataUrl: ticketImageDataUrl
        });
        if (emailSent) {
          // Also open WhatsApp chat to send ticket details to the user's phone
          if (selectedUser?.phoneNumber) {
            sendTicketViaWhatsApp(ticketData, selectedUser.phoneNumber);
          } else {
            console.warn('Selected user has no phoneNumber field; skipping WhatsApp ticket send');
          }

          await Swal.fire({
            icon: 'success',
            title: 'Ticket sent',
            text: `Ticket generated and sent successfully to ${selectedUser.email}!`,
            confirmButtonColor: '#10b981'
          });
        } else {
          await Swal.fire({
            icon: 'warning',
            title: 'Email not configured',
            html: `Ticket generated successfully!<br/>Ticket ID: <strong>${ticketId}</strong><br/><br/>Note: Email sending is not configured. Please configure EmailJS in <strong>emailService.js</strong>.`,
            confirmButtonColor: '#f59e0b'
          });
        }
      } catch (emailError) {
        console.error('Error sending email:', emailError);
        await Swal.fire({
          icon: 'error',
          title: 'Email error',
          html: `Ticket generated successfully!<br/>Ticket ID: <strong>${ticketId}</strong><br/><br/>Note: Email sending failed. Please check your EmailJS configuration.`,
          confirmButtonColor: '#b91c1c'
        });
      }
      
      // Reset form
      setSelectedUser('');
      setUserSearch('');
      setEventType(eventSettings?.eventName || defaultEventSettings.eventName);
      setNotes('');
      setPreviewTicketData(null);
      setPreviewQRCode(null);
    } catch (error) {
      console.error('Error generating ticket:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error generating ticket. Please try again.',
        confirmButtonColor: '#b91c1c'
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleMassGenerateTickets = async () => {
    if (selectedUsersForMass.length === 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'No users selected',
        text: 'Please select at least one user for mass ticket generation.',
        confirmButtonColor: '#f59e0b'
      });
      return;
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Generate tickets?',
      text: `Generate tickets for ${selectedUsersForMass.length} user(s)?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, generate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    setGeneratingMass(true);
    let successCount = 0;
    let skippedCount = 0;
    const errors = [];

    for (const user of selectedUsersForMass) {
      try {
        // Check if ticket already exists
        const ticketExists = await checkTicketExists(user.email);
        if (ticketExists) {
          skippedCount++;
          continue;
        }

        const ticketId = generateTicketId();
        const qrCodeDataUrl = await generateQRCode(ticketId);
        
        const ticketData = {
          ticketId,
          registrationId: user.id,
          userId: user.id || user.email,
          userName: user.fullName || user.email,
          userEmail: user.email,
          eventType,
          eventName: eventSettings?.eventName || defaultEventSettings.eventName,
          eventDate: eventSettings?.eventDate || defaultEventSettings.eventDate,
          eventTime: eventSettings?.eventTime || defaultEventSettings.eventTime,
          eventLocation: eventSettings?.eventLocation || defaultEventSettings.eventLocation,
          status: 'active',
          used: false,
          qrCodeDataUrl: qrCodeDataUrl,
          notes: notes || '',
          generatedAt: serverTimestamp(),
          generatedBy: user?.email || 'admin'
        };

        // Save ticket to Firestore
        await addDoc(collection(db, 'tickets'), ticketData);
        
        // Send email with ticket
        try {
          await sendTicketEmail({
            ...ticketData,
            qrCodeDataUrl: qrCodeDataUrl
          });
        } catch (emailError) {
          console.error(`Error sending email to ${user.email}:`, emailError);
        }
        
        successCount++;
      } catch (error) {
        console.error(`Error generating ticket for ${user.email}:`, error);
        errors.push(user.fullName || user.email);
      }
    }

    setGeneratingMass(false);
    setSelectedUsersForMass([]);
    
    let htmlMessage = `<p><strong>Successfully generated:</strong> ${successCount}</p>`;
    if (skippedCount > 0) {
      htmlMessage += `<p><strong>Skipped (already have tickets):</strong> ${skippedCount}</p>`;
    }
    if (errors.length > 0) {
      htmlMessage += `<p><strong>Errors:</strong> ${errors.length}</p>`;
    }

    await Swal.fire({
      icon: 'success',
      title: 'Mass generation completed',
      html: htmlMessage,
      confirmButtonColor: '#10b981'
    });
  };

  const toggleUserForMass = (user) => {
    setSelectedUsersForMass(prev => {
      const exists = prev.find(u => u.id === user.id);
      if (exists) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSelectAll = () => {
    // Select only users who don't have a ticket yet
    const usersWithoutTickets = filteredUsers.filter(reg => 
      !tickets.some(t => t.userEmail === reg.email)
    );
    setSelectedUsersForMass(usersWithoutTickets);
  };

  const handleDeselectAll = () => {
    setSelectedUsersForMass([]);
  };

  const handleToggleTicketStatus = async (ticket) => {
    if (!ticket?.id) return;
    const newStatus = ticket.status === 'disabled' ? 'active' : 'disabled';
    try {
      const ticketRef = doc(db, 'tickets', ticket.id);
      await updateDoc(ticketRef, {
        status: newStatus,
        statusUpdatedAt: serverTimestamp(),
        statusUpdatedBy: user?.email || 'admin'
      });
      setOpenTicketActionId(null);
      await Swal.fire({
        icon: 'success',
        title: 'Ticket updated',
        text: `Ticket ${newStatus === 'disabled' ? 'disabled' : 'enabled'} successfully.`,
        confirmButtonColor: '#10b981'
      });
    } catch (error) {
      console.error('Error updating ticket status:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to update ticket status. Please try again.',
        confirmButtonColor: '#b91c1c'
      });
    }
  };

  const handleDeleteTicket = async (ticket) => {
    if (!ticket?.id) return;
    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: 'Delete ticket?',
      text: `Delete ticket ${ticket.ticketId}? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'tickets', ticket.id));

      if (ticket.registrationId) {
        try {
          const registrationRef = doc(db, 'registrations', ticket.registrationId);
          await updateDoc(registrationRef, {
            ticketId: null,
            status: 'paid',
            ticketDeletedAt: serverTimestamp()
          });
        } catch (regError) {
          console.error('Error resetting registration after ticket deletion:', regError);
        }
      }

      setOpenTicketActionId(null);
      await Swal.fire({
        icon: 'success',
        title: 'Ticket deleted',
        text: 'Ticket deleted successfully.',
        confirmButtonColor: '#10b981'
      });
    } catch (error) {
      console.error('Error deleting ticket:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to delete ticket. Please try again.',
        confirmButtonColor: '#b91c1c'
      });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('adminUser');
      sessionStorage.removeItem('adminUser');
      navigate('/admin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNavClick = (path) => {
    closeSidebar();
    navigate(path);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) > 1 ? 's' : ''} ago`;
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusBadge = (status, used) => {
    if (used) {
      return { text: 'Checked In', bg: '#E0F7FA', color: '#0D9488' };
    }
    const statusMap = {
      'active': { text: 'Active', bg: '#DBFCE4', color: '#15803D' },
      'sent': { text: 'Active', bg: '#DBFCE4', color: '#15803D' },
      'disabled': { text: 'Disabled', bg: '#FEE2E2', color: '#B91C1C' },
      'pending': { text: 'Pending', bg: '#FEF9C3', color: '#A16207' }
    };
    return statusMap[status] || { text: status || 'Unknown', bg: '#F3F4F6', color: '#374151' };
  };

  const displayedTickets = showAllTickets ? tickets : tickets.slice(0, 5);

  return (
    <div className="generate-ticket-container">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}
      
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <button className="sidebar-close-button" onClick={closeSidebar} aria-label="Close menu">
          <CloseIcon />
        </button>
        
        <div className="sidebar-logo">
        <div className="logo-icon">
            <img
              src={process.env.PUBLIC_URL + '/logo.jpg'}
              alt="Mountain Top University"
              className="admin-dashboard-logo-image"
            />
          </div>
          <div className="logo-text">
            <div className="logo-title">MTU Alumni</div>
            <div className="logo-subtitle">Event Management</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => handleNavClick('/admin/dashboard')}>
            <DashboardIcon />
            <span>Dashboard</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/registrations')}>
            <RegistrationsIcon />
            <span>Registrations</span>
          </div>
          <div className="nav-item active" onClick={closeSidebar}>
            <TicketIcon />
            <span>Generate Ticket</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/checkin')}>
            <CheckInIcon />
            <span>Check-in</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/broadcasts')}>
            <BroadcastIcon />
            <span>Email Broadcast</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/dashboard?export=1')}>
            <ExportIcon />
            <span>Export Data</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/settings')}>
            <SettingsIcon />
            <span>Settings</span>
          </div>
        </nav>

        <div className="sidebar-user">
          <div className="user-info">
            <div className="user-avatar">
              {getUserInitials(user?.email || 'Admin User')}
            </div>
            <div className="user-details">
              <div className="user-name">Admin User</div>
              <div className="user-email">{user?.email || 'admin@mtu.edu'}</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <LogoutIcon />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="generate-ticket-main">
        <div className="generate-ticket-content">
          <div className="page-header">
            <div className="page-header-left">
              <h2 className="page-title">Generate New Ticket</h2>
              <p className="page-subtitle">Create and send tickets to your users instantly</p>
            </div>
            <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle menu">
              <HamburgerIcon />
            </button>
          </div>

          <div className="ticket-form-section">
            <div className="form-card">
              <div className="form-header">
                <h3 className="form-title">Ticket Details</h3>
                <p className="form-subtitle">Fill in the information to generate a ticket</p>
              </div>

              <form className="ticket-form">
                <div className="form-group">
                  <div className="form-label-row">
                    <label className="form-label">Select User</label>
                    <div className="mass-generation-controls">
                      <label className="mass-generation-toggle">
                        <input
                          type="checkbox"
                          checked={massGenerationMode}
                          onChange={(e) => {
                            setMassGenerationMode(e.target.checked);
                            setSelectedUser('');
                            setUserSearch('');
                            setSelectedUsersForMass([]);
                          }}
                        />
                        <span>Mass Generation Mode</span>
                      </label>
                      {massGenerationMode && (
                        <div className="selection-buttons">
                          <button 
                            type="button" 
                            className="selection-btn select-all"
                            onClick={handleSelectAll}
                            title="Select all users without tickets"
                          >
                            Select All
                          </button>
                          <button 
                            type="button" 
                            className="selection-btn deselect-all"
                            onClick={handleDeselectAll}
                          >
                            Deselect All
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="user-select-wrapper">
                    <input
                      type="text"
                      className="form-input"
                      placeholder={massGenerationMode ? "Search users to select multiple..." : "Search or select user..."}
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                    />
                    <div className="dropdown-icon">
                      <ChevronDownIcon />
                    </div>
                    {showUserDropdown && filteredUsers.length > 0 && (
                      <div className="user-dropdown">
                        {filteredUsers.map((reg) => {
                          const isSelected = massGenerationMode 
                            ? selectedUsersForMass.some(u => u.id === reg.id)
                            : selectedUser?.id === reg.id;
                          const hasTicket = tickets.some(t => t.userEmail === reg.email);
                          
                          return (
                            <div
                              key={reg.id}
                              className={`user-dropdown-item ${isSelected ? 'selected' : ''} ${hasTicket ? 'has-ticket' : ''}`}
                              onClick={() => {
                                if (massGenerationMode) {
                                  toggleUserForMass(reg);
                                } else {
                                  handleUserSelect(reg);
                                }
                              }}
                            >
                              {massGenerationMode && (
                                <div className="checkbox-wrapper">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleUserForMass(reg)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              )}
                              <div className="user-dropdown-avatar">
                                {getUserInitials(reg.fullName || reg.email)}
                              </div>
                              <div className="user-dropdown-info">
                                <div className="user-dropdown-name">
                                  {reg.fullName || reg.email}
                                  {hasTicket && <span className="ticket-badge">Ticket Exists</span>}
                                </div>
                                <div className="user-dropdown-email">{reg.email}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {filteredUsers.length === 0 && (
                      <div className="no-users-message">
                        No paid registrations found. Only users with "paid" status can receive tickets.
                      </div>
                    )}
                  </div>
                  {massGenerationMode && selectedUsersForMass.length > 0 && (
                    <div className="selected-users-count">
                      {selectedUsersForMass.length} user(s) selected for mass ticket generation
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Event Type</label>
                  <select
                    className="form-select"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                  >
                    <option value={eventSettings?.eventName || defaultEventSettings.eventName}>
                      {eventSettings?.eventName || defaultEventSettings.eventName}
                    </option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Additional Notes (Optional)</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Add any special instructions or notes..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="4"
                  />
                </div>

                {massGenerationMode ? (
                  <button
                    type="button"
                    className="generate-button mass-generate-button"
                    onClick={handleMassGenerateTickets}
                    disabled={generatingMass || selectedUsersForMass.length === 0}
                  >
                    <SendIcon />
                    <span>
                      {generatingMass 
                        ? `Generating ${selectedUsersForMass.length} tickets...` 
                        : `Generate ${selectedUsersForMass.length} Ticket(s)`}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="generate-button"
                    onClick={handleGenerateTicket}
                    disabled={generating || !selectedUser}
                  >
                    <SendIcon />
                    <span>{generating ? 'Generating...' : 'Generate Ticket'}</span>
                  </button>
                )}
              </form>
            </div>

            <div className="preview-card">
              <div className="preview-header">
                <h3 className="preview-title">Ticket Preview</h3>
                <p className="preview-subtitle">Your generated ticket will appear here</p>
              </div>
              <div className="preview-content">
                {previewTicketData && previewQRCode ? (
                  <div className="ticket-preview-wrapper" ref={previewTicketRef}>
                    <Ticket
                      ticketData={previewTicketData}
                      qrCodeDataUrl={previewQRCode}
                      eventSettings={eventSettings}
                    />
                  </div>
                ) : (
                  <>
                    <div className="preview-placeholder-icon">
                      <TicketIcon />
                    </div>
                    <p className="preview-placeholder-text">
                      No ticket preview yet<br />
                      Select a user to see the ticket preview
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="recent-tickets-section">
            <div className="recent-tickets-header">
              <div>
                <h3 className="recent-tickets-title">
                  {showAllTickets ? 'All Tickets' : 'Recent Tickets'}
                </h3>
                <p className="recent-tickets-subtitle">
                  {showAllTickets ? 'Showing every generated ticket' : 'Last 5 generated tickets'}
                </p>
              </div>
              {tickets.length > 5 && (
                <button
                  className="view-all-button"
                  onClick={() => setShowAllTickets(prev => !prev)}
                >
                  {showAllTickets ? 'Show Latest 5' : 'View All'}
                </button>
              )}
            </div>

            {loading ? (
              <div className="skeleton-table">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton-row"></div>
                ))}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="tickets-table-wrapper">
                  <table className="tickets-table">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>User</th>
                        <th>Event Type</th>
                        <th>Generated</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedTickets.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="empty-state">No tickets generated yet</td>
                        </tr>
                      ) : (
                        displayedTickets.map((ticket) => {
                          const statusBadge = getStatusBadge(ticket.status, ticket.used);
                          return (
                            <tr key={ticket.id}>
                              <td>{ticket.ticketId}</td>
                              <td>
                                <div className="table-user-cell">
                                  <div className="table-avatar">
                                    {getUserInitials(ticket.userName)}
                                  </div>
                                  <span>{ticket.userName}</span>
                                </div>
                              </td>
                              <td>{ticket.eventType}</td>
                              <td>{getTimeAgo(ticket.generatedAt)}</td>
                              <td>
                                <span
                                  className="status-badge"
                                  style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                                >
                                  {statusBadge.text}
                                </span>
                              </td>
                              <td>
                                <div 
                                  className="ticket-action-menu-container"
                                  data-ticket-id={ticket.id}
                                >
                                  <button
                                    className="ticket-action-menu-button"
                                    onClick={() =>
                                      setOpenTicketActionId(
                                        openTicketActionId === ticket.id ? null : ticket.id
                                      )
                                    }
                                  >
                                    <MoreIcon />
                                  </button>
                                  {openTicketActionId === ticket.id && (
                                    <div className="ticket-action-menu-dropdown open-upward">
                                      <button
                                        className="ticket-action-menu-item"
                                        onClick={() => handleToggleTicketStatus(ticket)}
                                      >
                                        {ticket.status === 'disabled' ? 'Enable Ticket' : 'Disable Ticket'}
                                      </button>
                                      <button
                                        className="ticket-action-menu-item delete"
                                        onClick={() => handleDeleteTicket(ticket)}
                                      >
                                        Delete Ticket
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="tickets-cards-mobile">
                  {displayedTickets.length === 0 ? (
                    <div className="empty-state-mobile">No tickets generated yet</div>
                  ) : (
                    displayedTickets.map((ticket) => {
                      const statusBadge = getStatusBadge(ticket.status, ticket.used);
                      return (
                        <div key={ticket.id} className="ticket-card-mobile">
                          <div className="ticket-card-header">
                            <div className="ticket-card-user">
                              <div className="ticket-card-avatar">
                                {getUserInitials(ticket.userName)}
                              </div>
                              <div className="ticket-card-user-info">
                                <div className="ticket-card-name">{ticket.userName}</div>
                                <div className="ticket-card-id">{ticket.ticketId}</div>
                              </div>
                            </div>
                            <div className="ticket-card-status">
                              <span
                                className="status-badge"
                                style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                              >
                                {statusBadge.text}
                              </span>
                            </div>
                          </div>
                          <div className="ticket-card-body">
                            <div className="ticket-card-row">
                              <span className="ticket-card-label">Event:</span>
                              <span className="ticket-card-value">{ticket.eventType}</span>
                            </div>
                            <div className="ticket-card-row">
                              <span className="ticket-card-label">Generated:</span>
                              <span className="ticket-card-value">{getTimeAgo(ticket.generatedAt)}</span>
                            </div>
                          </div>
                          <div className="ticket-card-actions">
                            <div 
                              className="ticket-action-menu-container"
                              data-ticket-id={ticket.id}
                            >
                              <button
                                className="ticket-action-menu-button"
                                onClick={() =>
                                  setOpenTicketActionId(
                                    openTicketActionId === ticket.id ? null : ticket.id
                                  )
                                }
                              >
                                <MoreIcon />
                              </button>
                              {openTicketActionId === ticket.id && (
                                <div className="ticket-action-menu-dropdown mobile-dropdown open-upward">
                                  <button
                                    className="ticket-action-menu-item"
                                    onClick={() => handleToggleTicketStatus(ticket)}
                                  >
                                    {ticket.status === 'disabled' ? 'Enable Ticket' : 'Disable Ticket'}
                                  </button>
                                  <button
                                    className="ticket-action-menu-item delete"
                                    onClick={() => handleDeleteTicket(ticket)}
                                  >
                                    Delete Ticket
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Icon Components
const DashboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 1C0 0.45 0.45 0 1 0H6C6.55 0 7 0.45 7 1V6C7 6.55 6.55 7 6 7H1C0.45 7 0 6.55 0 6V1ZM9 1C9 0.45 9.45 0 10 0H15C15.55 0 16 0.45 16 1V6C16 6.55 15.55 7 15 7H10C9.45 7 9 6.55 9 6V1ZM0 10C0 9.45 0.45 9 1 9H6C6.55 9 7 9.45 7 10V15C7 15.55 6.55 16 6 16H1C0.45 16 0 15.55 0 15V10ZM9 10C9 9.45 9.45 9 10 9H15C15.55 9 16 9.45 16 10V15C16 15.55 15.55 16 15 16H10C9.45 16 9 15.55 9 15V10Z" fill="currentColor"/>
  </svg>
);

const RegistrationsIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
    <path d="M10 0C7.24 0 5 2.24 5 5C5 7.76 7.24 10 10 10C12.76 10 15 7.76 15 5C15 2.24 12.76 0 10 0ZM10 13C6.67 13 0 14.67 0 18V20H20V18C20 14.67 13.33 13 10 13Z" fill="currentColor"/>
  </svg>
);

const TicketIcon = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
    <path d="M0 2C0 1.45 0.45 1 1 1H17C17.55 1 18 1.45 18 2V4C17.45 4 17 4.45 17 5C17 5.55 17.45 6 18 6V8C18 8.55 17.55 9 17 9H1C0.45 9 0 8.55 0 8V6C0.55 6 1 5.55 1 5C1 4.45 0.55 4 0 4V2Z" fill="currentColor"/>
  </svg>
);

const CheckInIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path d="M7 0C3.13 0 0 3.13 0 7C0 10.87 3.13 14 7 14C10.87 14 14 10.87 14 7C14 3.13 10.87 0 7 0ZM10.5 6L6.5 10L3.5 7L4.5 6L6.5 8L9.5 5L10.5 6Z" fill="currentColor"/>
  </svg>
);

const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 0V16H16V0H0ZM2 2H14V14H2V2ZM4 4H12V6H4V4ZM4 8H12V10H4V8ZM4 12H8V14H4V12Z" fill="currentColor"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 14H2C1.45 14 1 13.55 1 13V3C1 2.45 1.45 2 2 2H6V0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H6V14ZM11.5 11L9.5 9L12 6.5H4V8.5H12L9.5 6L11.5 4L16 8.5L11.5 11Z" fill="currentColor"/>
  </svg>
);

const BroadcastIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2H18C19.1 2 20 2.9 20 4V12C20 13.1 19.1 14 18 14H2C0.9 14 0 13.1 0 12V4C0 2.9 0.9 2 2 2ZM18 4L10 8.5L2 4V4L10 0.5L18 4ZM2 6V12H18V6L10 10.5L2 6Z" fill="currentColor" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 12.5C11.38 12.5 12.5 11.38 12.5 10C12.5 8.62 11.38 7.5 10 7.5C8.62 7.5 7.5 8.62 7.5 10C7.5 11.38 8.62 12.5 10 12.5ZM15.92 6.42L14.5 5C14.5 3.62 13.38 2.5 12 2.5L10.58 1.08C9.2 1.08 8.08 2.2 8.08 3.58L6.66 5C5.28 5 4.16 6.12 4.16 7.5L2.74 8.92C2.74 10.3 3.86 11.42 5.24 11.42L6.66 12.84C6.66 14.22 7.78 15.34 9.16 15.34L10.58 16.76C11.96 16.76 13.08 15.64 13.08 14.26L14.5 12.84C15.88 12.84 17 11.72 17 10.34L18.42 8.92C18.42 7.54 17.3 6.42 15.92 6.42Z" fill="currentColor"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
    <path d="M0 2L18 8L0 14V2Z" fill="currentColor"/>
  </svg>
);

const MoreIcon = () => (
  <svg width="4" height="16" viewBox="0 0 4 16" fill="none">
    <circle cx="2" cy="2" r="1.5" fill="currentColor"/>
    <circle cx="2" cy="8" r="1.5" fill="currentColor"/>
    <circle cx="2" cy="14" r="1.5" fill="currentColor"/>
  </svg>
);

const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default GenerateTicketPage;

